import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Input,
  Button,
  Avatar,
  Tag,
  Spin,
  Empty,
  message,
  Typography,
  Divider,
} from 'antd';
import {
  SendOutlined,
  UserOutlined,
  RobotOutlined,
  ClearOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { expertsApi } from '../services/api';
import './ExpertChat.css';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const { Title, Text } = Typography;
const { TextArea } = Input;

function ExpertChat() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const messagesEndRef = useRef(null);
  
  const [expert, setExpert] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [sessionId, setSessionId] = useState(`session_${Date.now()}`);

  // 获取专家信息
  useEffect(() => {
    fetchExpert();
    fetchSuggestions();
  }, [id]);

  // 自动滚动到底部
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchExpert = async () => {
    try {
      const res = await expertsApi.getExpert(id);
      setExpert(res.data.data);
    } catch (error) {
      message.error('获取专家信息失败');
    }
  };

  const fetchSuggestions = async () => {
    try {
      const res = await expertsApi.getExpertSuggestions(id);
      setSuggestions(res.data.data || []);
    } catch (error) {
      console.error('获取建议失败:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async () => {
    if (!inputValue.trim()) return;
    
    const userMessage = inputValue.trim();
    setInputValue('');
    
    // 添加用户消息
    setMessages(prev => [...prev, {
      role: 'user',
      content: userMessage,
      timestamp: Date.now(),
    }]);
    
    setLoading(true);
    
    try {
      const res = await expertsApi.chatWithExpert(id, {
        message: userMessage,
        sessionId,
      });
      
      if (res.data.success) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: res.data.data.reply,
          timestamp: Date.now(),
          model: res.data.data.model,
        }]);
      } else {
        message.error(res.data.message || '对话失败');
      }
    } catch (error) {
      console.error('聊天失败:', error);
      const msg = error.response?.data?.message
        || (error.code === 'ECONNABORTED' ? 'AI响应超时，请重试' : '网络错误，请稍后重试');
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = async () => {
    try {
      await expertsApi.clearExpertChat(id, { sessionId });
      setMessages([]);
      setSessionId(`session_${Date.now()}`);
      message.success('会话已清空');
    } catch (error) {
      message.error('清空失败');
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setInputValue(suggestion);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!expert) {
    return (
      <div className="expert-chat-loading">
        <Spin size="large" tip="加载中..." />
      </div>
    );
  }

  return (
    <div className="expert-chat-page">
      {/* 头部 */}
      <div className="chat-header">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/experts')}
          className="back-btn"
        >
          返回
        </Button>
        
        <div className="expert-info">
          <Avatar size={48} icon={<RobotOutlined />} />
          <div className="expert-meta">
            <Title level={4} className="expert-name">{expert.name}</Title>
            <Tag color="blue">{expert.type}</Tag>
          </div>
        </div>
        
        <Button
          icon={<ClearOutlined />}
          onClick={handleClear}
          className="clear-btn"
        >
          清空会话
        </Button>
      </div>

      {/* 聊天区域 */}
      <div className="chat-container">
        {messages.length === 0 ? (
          <div className="chat-welcome">
            <Avatar size={80} icon={<RobotOutlined />} className="welcome-avatar" />
            <Title level={3}>你好，我是{expert.name}</Title>
            <Text type="secondary" className="welcome-desc">
              {expert.bio}
            </Text>
            
            {suggestions.length > 0 && (
              <div className="suggestions">
                <Text type="secondary">试试这样问我：</Text>
                <div className="suggestion-list">
                  {suggestions.map((suggestion, index) => (
                    <Button
                      key={index}
                      type="dashed"
                      size="small"
                      onClick={() => handleSuggestionClick(suggestion)}
                      className="suggestion-btn"
                    >
                      {suggestion}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="messages-list">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`message-item ${msg.role}`}
              >
                <div className="message-avatar">
                  {msg.role === 'user' ? (
                    <Avatar icon={<UserOutlined />} />
                  ) : (
                    <Avatar icon={<RobotOutlined />} style={{ background: '#7c3aed' }} />
                  )}
                </div>
                <div className="message-content">
                  <div className="message-bubble">
                    {msg.role === 'user' ? (
                      msg.content
                    ) : (
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {msg.content || ''}
                      </ReactMarkdown>
                    )}
                  </div>
                  {msg.model && (
                    <Text type="secondary" className="model-tag">
                      由 {msg.model} 生成
                    </Text>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="message-item assistant">
                <div className="message-avatar">
                  <Avatar icon={<RobotOutlined />} style={{ background: '#7c3aed' }} />
                </div>
                <div className="message-content">
                  <Spin size="small" tip="思考中..." />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* 输入区域 */}
      <div className="chat-input-area">
        <div className="input-wrapper">
          <TextArea
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={`向${expert.name}提问...`}
            autoSize={{ minRows: 1, maxRows: 4 }}
            className="chat-input"
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={handleSend}
            loading={loading}
            className="send-btn"
          >
            发送
          </Button>
        </div>
        <Text type="secondary" className="input-hint">
          按 Enter 发送，Shift + Enter 换行
        </Text>
      </div>
    </div>
  );
}

export default ExpertChat;
